import { useRef, useEffect, useState } from 'react'

const useDebounce = (fn, delay) => {
  const [isPending, setIsPending] = useState(false)
  const timeoutRef = useRef(null)
  const pendingCallRef = useRef(null) // 🆕 guarda a chamada mais recente enquanto uma execução está em andamento

  const runFn = (args, resolve, reject) => {
    setIsPending(true)
    Promise.resolve(fn(...args))
      .then(resolve, reject)
      .finally(() => {
        setIsPending(false)
        // 🆕 se chegou uma chamada nova enquanto essa executava, dispara ela agora
        if (pendingCallRef.current) {
          const {
            args: nextArgs,
            resolve: nextResolve,
            reject: nextReject,
          } = pendingCallRef.current
          pendingCallRef.current = null
          runFn(nextArgs, nextResolve, nextReject)
        }
      })
  }

  const debouncedFn = (...args) => {
    return new Promise((resolve, reject) => {
      window.clearTimeout(timeoutRef.current)

      timeoutRef.current = window.setTimeout(() => {
        if (isPending) {
          // 🆕 guarda a chamada mais recente em vez de descartar
          pendingCallRef.current = { args, resolve, reject }
          return
        }
        runFn(args, resolve, reject)
      }, delay)
    })
  }

  const flush = (...args) => {
    window.clearTimeout(timeoutRef.current)

    return new Promise((resolve, reject) => {
      if (isPending) {
        pendingCallRef.current = { args, resolve, reject }
        return
      }

      runFn(args, resolve, reject)
    })
  }

  useEffect(() => {
    return () => window.clearTimeout(timeoutRef.current)
  }, [])

  return { debouncedFn, flush, isPending }
}

export default useDebounce
